import React, { useEffect, useState } from "react";
import { Drawer, Button, Grid, Input, message } from "antd";
import { Link } from "react-router-dom";
import {
  RightOutlined,
  MenuOutlined,
  CloseOutlined,
  CheckCircleTwoTone,
  CloseCircleTwoTone,
  FacebookOutlined,
  LinkedinOutlined,
  TwitterOutlined,
  YoutubeOutlined,
  EnvironmentTwoTone,
  PhoneTwoTone,
  MailTwoTone,
} from "@ant-design/icons";
import AOS from "aos";
import "aos/dist/aos.css";

import "./styles.scss";
import logo from "../../Assets/Logos/ibas_logo.jpeg";
import { verifyCertificate } from "../../Constants/Api/functions/function";

const VerifyCertificate = () => {
  const { useBreakpoint } = Grid;
  const screens = useBreakpoint();
  const isMobile = !screens.lg;

  const [drawerVisible, setDrawerVisible] = useState(false);
  const [certNumber, setCertNumber] = useState("");
  const [certResult, setCertResult] = useState(null);
  const [isVerifyingCert, setIsVerifyingCert] = useState(false);

  useEffect(() => {
    AOS.init({
      duration: 800,
      easing: "ease-in-out",
      once: true
    });
  }, []);

  const showDrawer = () => setDrawerVisible(true);
  const closeDrawer = () => setDrawerVisible(false);

  const handleVerify = async () => {
    if (!certNumber.trim()) {
      message.warning("Please enter a certificate number to verify.");
      return;
    }
    setIsVerifyingCert(true);
    try {
      const res = await verifyCertificate(certNumber);
      if (res && res.verified) {
        setCertResult(res);
        message.success("Certificate verified successfully!");
      } else {
        setCertResult({ verified: false, message: res?.message || "Certificate not found." });
        message.error("Invalid certificate number.");
      }
    } catch (err) {
      console.error(err);
      setCertResult({ verified: false, message: "An error occurred during verification." });
      message.error("Failed to verify certificate.");
    } finally {
      setIsVerifyingCert(false);
    }
  };

  const handleHomeNavClick = (sectionId) => {
    window.location.href = `/#${sectionId}`;
    if (isMobile) closeDrawer();
  };

  return (
    <div className="verify-page landing-page">
      <header className="site-header">
        <div className="header-conteiner">
          <div className="logo">
            <Link to="/" aria-label="IBAS Home">
              <img src={logo} alt="IBAS Logo" />
            </Link>
          </div>

          {/* hamburger icon, mobile only */}
          {isMobile && (
            <Button
              type="text"
              className="mobile-menu-icon"
              onClick={showDrawer}
              aria-label="Open Menu"
              icon={<MenuOutlined />}
            />
          )}

          {/* desktop navigation */}
          <div className="btns">
            <Button type="link" onClick={() => handleHomeNavClick("landinghome")}>
              Home
            </Button>
            <Link to="/verify-certificate">
              <Button type="link" className="active">Verify Certificate</Button>
            </Link>
            <Button type="link" onClick={() => handleHomeNavClick("course-finder")}>
              Course Finder
            </Button>
            <Button type="link" onClick={() => handleHomeNavClick("courses-section")}>
              Courses
            </Button>
            <Button type="link" onClick={() => handleHomeNavClick("faculties")}>
              Faculties
            </Button>
            <Button type="link" onClick={() => handleHomeNavClick("professional-programs")}>
              Professional Programs
            </Button>
            <Button type="link" onClick={() => handleHomeNavClick("blog-news")}>
              Blog
            </Button>
            <Button type="link" onClick={() => handleHomeNavClick("contact-us")}>
              Contact Us
            </Button>
            <Link className="signin-btn" to="/register">
              Apply Now
            </Link>
            <Link className="signin-btn btn-solid-gold" to="/login">
              LMS Login
            </Link>
          </div>
        </div>

        {/* mobile drawer */}
        {isMobile && (
          <Drawer
            placement="right"
            closable={true}
            onClose={closeDrawer}
            open={drawerVisible}
            closeIcon={<CloseOutlined />}
            className="mobile-menu-drawer"
            width={280}
          >
            <div className="drawer-menu">
              <Button block type="link" onClick={() => handleHomeNavClick("landinghome")}>
                <span>Home</span>
                <RightOutlined />
              </Button>
              <Link to="/verify-certificate">
                <Button block type="link" onClick={closeDrawer} className="active">
                  <span>Verify Certificate</span>
                  <RightOutlined />
                </Button>
              </Link>
              <Button block type="link" onClick={() => handleHomeNavClick("course-finder")}>
                <span>Course Finder</span>
                <RightOutlined />
              </Button>
              <Button block type="link" onClick={() => handleHomeNavClick("courses-section")}>
                <span>Courses</span>
                <RightOutlined />
              </Button>
              <Button block type="link" onClick={() => handleHomeNavClick("faculties")}>
                <span>Faculties</span>
                <RightOutlined />
              </Button>
              <Button block type="link" onClick={() => handleHomeNavClick("professional-programs")}>
                <span>Professional Programs</span>
                <RightOutlined />
              </Button>
              <Button block type="link" onClick={() => handleHomeNavClick("blog-news")}>
                <span>Blog & News</span>
                <RightOutlined />
              </Button>
              <Button block type="link" onClick={() => handleHomeNavClick("contact-us")}>
                <span>Contact Us</span>
                <RightOutlined />
              </Button>
              <Link to="/register">
                <Button block type="link" onClick={closeDrawer}>
                  <span>Apply Now</span>
                  <RightOutlined />
                </Button>
              </Link>
            </div>

            <div className="drawer-footer">
              <Link to="/login">
                <Button block type="primary" size="large" onClick={closeDrawer}>
                  LMS Login
                </Button>
              </Link>
            </div>
          </Drawer>
        )}
      </header>

      <div className="verify-body">
        <section className="verify-hero" data-aos="fade-up">
          <div className="hero-content">
            <h1>Certificate Verification</h1>
            <p>Verify the authenticity of qualifications and academic credentials issued by IBAS.</p>
          </div>
        </section>

        <section className="verify-search-section" data-aos="fade-up" data-aos-delay="200">
          <div className="verify-container">
            <div className="verify-card">
              <p className="instruction-text">
                Please enter the unique certificate registration number printed on the certificate to check its validity and verify student achievements.
              </p>
              
              <div className="verify-input-group">
                <Input
                  size="large"
                  placeholder="Enter Certificate Number (e.g., EX19/10683)"
                  value={certNumber}
                  onChange={(e) => {
                    setCertNumber(e.target.value);
                    setCertResult(null);
                  }}
                  onPressEnter={handleVerify}
                  style={{ borderRadius: '8px' }}
                />
                <Button
                  type="primary"
                  size="large"
                  onClick={handleVerify}
                  loading={isVerifyingCert}
                  style={{ borderRadius: '8px', fontWeight: 600 }}
                >
                  Verify Now
                </Button>
              </div>

              {certResult && (
                <div className={`verification-result-box ${certResult.verified ? 'verified' : 'unverified'} animate-fade-in`}>
                  {certResult.verified ? (
                    <>
                      <div className="result-header">
                        <CheckCircleTwoTone twoToneColor="#52c41a" className="result-icon" />
                        <h3>Certificate Verified Successfully</h3>
                      </div>
                      <div className="result-details">
                        <div className="result-row">
                          <span className="label">Certificate Number:</span>
                          <span className="value"><strong>{certResult.certificateNumber}</strong></span>
                        </div>
                        <div className="result-row">
                          <span className="label">Student Name:</span>
                          <span className="value"><strong>{certResult.studentName}</strong></span>
                        </div>
                        <div className="result-row">
                          <span className="label">Course Name:</span>
                          <span className="value">{certResult.courseName}</span>
                        </div>
                        <div className="result-row">
                          <span className="label">Issued Date:</span>
                          <span className="value">
                            {certResult.issueDate ? new Date(certResult.issueDate).toLocaleDateString('en-US', {
                              year: 'numeric',
                              month: 'long',
                              day: 'numeric'
                            }) : 'N/A'}
                          </span>
                        </div>
                        {certResult.certificateUrl && (
                          <div className="result-action">
                            <a
                              href={certResult.certificateUrl}
                              target="_blank"
                              rel="noreferrer"
                              className="view-cert-btn"
                            >
                              View Certificate Document
                            </a>
                          </div>
                        )}
                      </div>
                    </>
                  ) : (
                    <div className="unverified-result">
                      <CloseCircleTwoTone twoToneColor="#f5222d" className="result-icon" />
                      <h3>Verification Failed</h3>
                      <p>{certResult.message || 'The certificate number entered is invalid or could not be found.'}</p>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </section>
      </div>

      <footer id="footer">
        <div className="footer-sec">
          <div className="logo">
            <a href="/">
              <img src={logo} alt="IBAS logo" />
            </a>
          </div>
          <div className="text">
            <p>
              Institute of Business and Accounting Studies (Pvt) Ltd (IBAS) is a government-registered higher education institute (PV No: 00264413), recognized and accredited by AAT Sri Lanka. We develop industry-ready leaders via premium interactive accounting, management, and technology systems.
            </p>
          </div>
          <div className="social-links-row">
            <a href="https://facebook.com" target="_blank" rel="noreferrer" className="social-circle"><FacebookOutlined /></a>
            <a href="https://linkedin.com" target="_blank" rel="noreferrer" className="social-circle"><LinkedinOutlined /></a>
            <a href="https://twitter.com" target="_blank" rel="noreferrer" className="social-circle"><TwitterOutlined /></a>
            <a href="https://youtube.com" target="_blank" rel="noreferrer" className="social-circle"><YoutubeOutlined /></a>
          </div>
        </div>

        <div className="footer-sec">
          <h3>Quick Links</h3>
          <div className="text footer-links">
            <p>
              <Button type="link" onClick={() => handleHomeNavClick("landinghome")}>
                Home
              </Button>
            </p>
            <p>
              <Link to="/verify-certificate">
                <Button type="link">Verify Certificate</Button>
              </Link>
            </p>
            <p>
              <Button type="link" onClick={() => handleHomeNavClick("course-finder")}>
                Course Finder mega-menu
              </Button>
            </p>
            <p>
              <Button type="link" onClick={() => handleHomeNavClick("courses-section")}>
                Popular Courses
              </Button>
            </p>
            <p>
              <Button type="link" onClick={() => handleHomeNavClick("faculties")}>
                distinguished Faculty
              </Button>
            </p>
            <p>
              <Button type="link" onClick={() => handleHomeNavClick("professional-programs")}>
                Accredited Exam prep
              </Button>
            </p>
            <p>
              <Link to="/login">
                <Button type="link">LMS Login Portal</Button>
              </Link>
            </p>
          </div>
        </div>

        <div className="footer-sec">
          <h3>Gampaha Head Office</h3>
          <div className="text text-contacts">
            <p>
              <span>
                <EnvironmentTwoTone twoToneColor="#f0b323" />
              </span>
              No. 04, Bauddhaloka Mawatha, Gampaha, Sri Lanka.
            </p>
            <p>
              <span>
                <PhoneTwoTone twoToneColor="#f0b323" />
              </span>
              071 216 3163 | 033 232 1966
            </p>
            <p>
              <span>
                <MailTwoTone twoToneColor="#f0b323" />
              </span>
              <a href="mailto:ibasinstitute@gmail.com">ibasinstitute@gmail.com</a>
            </p>
          </div>
        </div>
      </footer>

      <section className="copy-right">
        <p>
          © 2026
          <a href="http://tuitionpanthiya.lk/" target="_blank" rel="noreferrer">
            {" "}
            IBAS Institute.
          </a>{" "}
          All Rights Reserved. Government Registered Higher Education Institute (PV No: 00264413).
        </p>

        <p>
          Designed & Developed by
          <a href="https://lionbit.cc/" target="_blank" rel="noreferrer">
            {" "}
            LionBit Solutions.
          </a>
        </p>
      </section>
    </div>
  );
};

export default VerifyCertificate;
